import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../services/authService';
import { firebaseService } from '../services/firebaseService';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoggingOut = useRef(false);

  const verifyFirebaseToken = async (idToken) => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005/api';
      const response = await axios.post(`${API_BASE_URL}/auth-firebase/verify-token`, {
        idToken
      });

      if (response.data.success) {
        const { user: userData } = response.data;
        // Store Firebase token
        localStorage.setItem('firebaseToken', idToken);
        localStorage.setItem('token', idToken); // Also store as token for compatibility
        authService.setAuthToken(idToken);
        setUser(userData);
        setLoading(false);
        return { success: true, user: userData };
      } else {
        // Backend returned success: false
        const errorMessage = response.data?.error || 'Authentication failed';
        setError(errorMessage);
        setLoading(false);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Firebase token verification failed:', error);
      localStorage.removeItem('firebaseToken');
      localStorage.removeItem('token');
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Authentication failed';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  const checkAuthStatus = async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      authService.removeAuthToken();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isInitialLoad = true;
    
    // Check for Firebase auth state
    const unsubscribe = firebaseService.onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in with Firebase
        isInitialLoad = false;
        try {
          const idToken = await firebaseUser.getIdToken();
          await verifyFirebaseToken(idToken);
        } catch (error) {
          console.error('Firebase auth state change error:', error);
          setLoading(false);
        }
      } else {
        // Firebase user is null
        // If we're logging out, don't try to re-authenticate
        if (isLoggingOut.current) {
          setUser(null);
          setLoading(false);
          return;
        }
        
        // Only try traditional auth on initial load, not after logout
        if (isInitialLoad) {
          isInitialLoad = false;
          const token = localStorage.getItem('token');
          
          if (token) {
            // Check if it's a Firebase token (longer than 200 chars) or JWT
            if (token.length < 200) {
              // Traditional JWT token - try to authenticate
              authService.setAuthToken(token);
              checkAuthStatus();
            } else {
              // Firebase token stored but Firebase user is null
              // Token is invalid or expired, clear it
              localStorage.removeItem('token');
              localStorage.removeItem('firebaseToken');
              authService.removeAuthToken();
              setUser(null);
              setLoading(false);
            }
          } else {
            // No token - user is logged out
            setUser(null);
            setLoading(false);
          }
        } else {
          // Not initial load and not logging out - Firebase user signed out
          // Clear everything
          localStorage.removeItem('token');
          localStorage.removeItem('firebaseToken');
          authService.removeAuthToken();
          setUser(null);
          setLoading(false);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (username, password) => {
    try {
      setError(null);
      const response = await authService.login(username, password);
      console.log('Login response:', response);
      const { token, user: userData } = response;
      
      console.log('Token:', token);
      console.log('User data:', userData);
      
      localStorage.setItem('token', token);
      authService.setAuthToken(token);
      setUser(userData);
      
      console.log('User set in context:', userData);
      
      return { success: true };
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
      return { success: false, error: error.response?.data?.message || 'Login failed' };
    }
  };

  const loginWithFirebaseEmail = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const { idToken } = await firebaseService.signInWithEmail(email, password);
      const result = await verifyFirebaseToken(idToken);
      return result;
    } catch (error) {
      // Extract error message from Firebase error object
      // The error from handleFirebaseError is now an Error object with message property
      let errorMessage = 'Wrong email or password. Please check your credentials and try again.';
      
      console.error('loginWithFirebaseEmail caught error:', error);
      console.error('Error type:', typeof error);
      console.error('Error message:', error?.message);
      console.error('Error code:', error?.code);
      
      if (error) {
        // Error object from handleFirebaseError should have a message property
        if (error.message) {
          errorMessage = error.message;
        }
        // Fallback: check for Firebase error codes directly
        else if (error.code) {
          const code = error.code;
          if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || 
              code === 'auth/invalid-credential' || code === 'auth/invalid-email') {
            errorMessage = 'Wrong email or password. Please check your credentials and try again.';
          }
        }
        // Check nested error structure (for REST API errors)
        else if (error.error) {
          if (error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error.code === 'INVALID_LOGIN_CREDENTIALS') {
            errorMessage = 'Wrong email or password. Please check your credentials and try again.';
          }
        }
      }
      
      console.error('Setting error message:', errorMessage);
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  const loginWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      const { idToken } = await firebaseService.signInWithGoogle();
      const result = await verifyFirebaseToken(idToken);
      return result;
    } catch (error) {
      setError(error.message || 'Google sign-in failed');
      setLoading(false);
      return { success: false, error: error.message || 'Google sign-in failed' };
    }
  };

  const logout = async () => {
    try {
      // Set flag to prevent re-authentication during logout
      isLoggingOut.current = true;
      
      // Clear state and tokens FIRST to prevent re-authentication
      localStorage.removeItem('token');
      localStorage.removeItem('firebaseToken');
      authService.removeAuthToken();
      setUser(null);
      setError(null);
      
      // Then sign out from Firebase (if signed in)
      const firebaseUser = firebaseService.getCurrentUser();
      if (firebaseUser) {
        await firebaseService.signOut();
      } else {
        // Try traditional logout if not Firebase user
    try {
      await authService.logout();
        } catch (error) {
          // Ignore logout errors if already logged out
          console.log('Logout request failed (may already be logged out):', error);
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Ensure state is cleared even if there's an error
      localStorage.removeItem('token');
      localStorage.removeItem('firebaseToken');
      authService.removeAuthToken();
      setUser(null);
      setError(null);
    } finally {
      // Reset flag after a short delay to allow auth state change to process
      setTimeout(() => {
        isLoggingOut.current = false;
      }, 100);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const updatedUser = await authService.updateProfile(profileData);
      setUser(updatedUser);
      return { success: true };
    } catch (error) {
      setError(error.response?.data?.message || 'Profile update failed');
      return { success: false, error: error.response?.data?.message || 'Profile update failed' };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      return { success: true };
    } catch (error) {
      setError(error.response?.data?.message || 'Password change failed');
      return { success: false, error: error.response?.data?.message || 'Password change failed' };
    }
  };

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user,
    loading,
    error,
    login, // Traditional JWT login
    loginWithFirebaseEmail, // Firebase email/password login
    loginWithGoogle, // Firebase Google OAuth login
    logout,
    updateProfile,
    changePassword,
    clearError,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.bridgebudgetRole === 'admin',
    isManager: user?.role === 'manager' || user?.bridgebudgetRole === 'planner',
    isEngineer: user?.role === 'engineer',
    isInspector: user?.role === 'inspector' || user?.role === 'user' || user?.bridgebudgetRole === 'inspector'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 